﻿import { useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  EmptyState,
  InlineStack,
  Layout,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import type { ContentAngle, Keyword, KeywordResourceType, KeywordSet } from '../../api/keywords';
import { useGenerateKeywords, useKeywordSets, useDeleteKeywordSet } from '../../hooks/useKeywords';
import { IconBadge } from '../shared/IconBadge';

const INTENT_TONES: Record<string, 'success' | 'info' | 'warning' | 'attention'> = {
  transactional: 'success',
  commercial:    'info',
  informational: 'warning',
  navigational:  'attention',
};

const DIFFICULTY_TONES: Record<string, 'success' | 'warning' | 'critical'> = {
  low:    'success',
  medium: 'warning',
  high:   'critical',
};

function KeywordTable({ keywords }: { keywords: Keyword[] }) {
  const rows = keywords.map((kw) => [
    kw.phrase,
    <Badge tone={INTENT_TONES[kw.intent] ?? 'info'}>{kw.intent}</Badge>,
    <Badge tone={DIFFICULTY_TONES[kw.difficulty] ?? 'info'}>{kw.difficulty}</Badge>,
    kw.monthly_volume_estimate,
  ]);

  return (
    <DataTable
      columnContentTypes={['text', 'text', 'text', 'text']}
      headings={['Keyword', 'Intent', 'Difficulty', 'Monthly Volume']}
      rows={rows}
    />
  );
}

function ContentAnglesCard({ angles }: { angles: ContentAngle[] }) {
  if (!angles.length) return null;
  return (
    <Card>
      <BlockStack gap="300">
        <Text variant="headingMd" as="h3">Content Angles</Text>
        {angles.map((a, i) => (
          <BlockStack key={i} gap="100">
            <Text variant="bodyMd" fontWeight="bold" as="p">{a.angle}</Text>
            <Text variant="bodySm" tone="subdued" as="p">{a.description}</Text>
          </BlockStack>
        ))}
      </BlockStack>
    </Card>
  );
}

function KeywordSetDetail({
  keywordSet,
  onBack,
  onDelete,
}: {
  keywordSet: KeywordSet;
  onBack: () => void;
  onDelete: (id: number) => void;
}) {
  return (
    <BlockStack gap="500">
      <InlineStack align="space-between" blockAlign="center">
        <Button onClick={onBack} variant="plain">← Back to list</Button>
        <Button tone="critical" onClick={() => onDelete(keywordSet.id)}>Delete</Button>
      </InlineStack>

      <Card>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="info">{keywordSet.resource_type_label}</Badge>
            <Text variant="headingLg" as="h2">{keywordSet.resource_title}</Text>
          </InlineStack>
          <Text variant="bodyMd" as="p">
            Primary keyword: <strong>{keywordSet.primary_keyword}</strong>
          </Text>
          <Text variant="bodySm" tone="subdued" as="p">
            {keywordSet.keyword_count} keywords Â· {keywordSet.ai_model}
          </Text>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h3">Keywords</Text>
          <KeywordTable keywords={keywordSet.keywords ?? []} />
        </BlockStack>
      </Card>

      <ContentAnglesCard angles={keywordSet.content_angles ?? []} />
    </BlockStack>
  );
}

function GenerateForm() {
  const generate = useGenerateKeywords();
  const [resourceType, setResourceType] = useState<KeywordResourceType>('store');
  const [resourceId, setResourceId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (resourceType !== 'store' && !resourceId) {
      setError('Resource ID is required for product and page keyword research.');
      return;
    }
    try {
      await generate.mutateAsync({
        resource_type: resourceType,
        resource_id:   resourceId ? parseInt(resourceId, 10) : undefined,
      });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to generate keywords.');
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h3">Generate Keyword Research</Text>
        {error && <Banner tone="critical">{error}</Banner>}
        <Select
          label="Resource type"
          options={[
            { label: 'Store-wide', value: 'store' },
            { label: 'Product', value: 'product' },
            { label: 'Page', value: 'page' },
          ]}
          value={resourceType}
          onChange={(v) => setResourceType(v as KeywordResourceType)}
        />
        {resourceType !== 'store' && (
          <TextField
            label={`Shopify ${resourceType} ID`}
            type="number"
            value={resourceId}
            onChange={setResourceId}
            autoComplete="off"
          />
        )}
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={generate.isPending}
        >
          Generate keywords
        </Button>
      </BlockStack>
    </Card>
  );
}

function KeywordSetList({
  onSelect,
}: {
  onSelect: (ks: KeywordSet) => void;
}) {
  const [resourceType, setResourceType] = useState<KeywordResourceType | undefined>(undefined);
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useKeywordSets(resourceType, page);
  const deleteKs = useDeleteKeywordSet();

  if (isLoading) return <Spinner />;
  if (error) return <Banner tone="critical">Failed to load keyword research.</Banner>;

  const sets = data?.data ?? [];
  const meta  = data?.meta;

  if (!sets.length) {
    return (
      <EmptyState
        heading="No keyword research yet"
        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      >
        <p>Generate keyword research for your products, pages, or store above.</p>
      </EmptyState>
    );
  }

  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Select
          label="Filter"
          labelHidden
          options={[
            { label: 'All types', value: '' },
            { label: 'Store', value: 'store' },
            { label: 'Product', value: 'product' },
            { label: 'Page', value: 'page' },
          ]}
          value={resourceType ?? ''}
          onChange={(v) => { setResourceType(v as KeywordResourceType || undefined); setPage(1); }}
        />
      </InlineStack>

      {sets.map((ks) => (
        <Card key={ks.id}>
          <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
            <InlineStack gap="300" blockAlign="center" wrap={false}>
              <IconBadge icon={SearchIcon} color="blue" />
              <BlockStack gap="100">
                <InlineStack gap="200">
                  <Badge tone="info">{ks.resource_type_label}</Badge>
                  <Text variant="bodyMd" fontWeight="semibold" as="span">{ks.resource_title}</Text>
                </InlineStack>
                <Text variant="bodySm" tone="subdued" as="p">
                  Primary: <em>{ks.primary_keyword}</em> Â· {ks.keyword_count} keywords
                </Text>
              </BlockStack>
            </InlineStack>
            <InlineStack gap="200">
              <Button onClick={() => onSelect(ks)}>View</Button>
              <Button tone="critical" onClick={() => deleteKs.mutate(ks.id)}>Delete</Button>
            </InlineStack>
          </InlineStack>
        </Card>
      ))}

      {meta && meta.last_page > 1 && (
        <InlineStack gap="200" align="center">
          <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Text as="span" variant="bodySm">{page} / {meta.last_page}</Text>
          <Button disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}

export function KeywordResearchPanel() {
  const [selected, setSelected] = useState<KeywordSet | null>(null);
  const deleteKs = useDeleteKeywordSet();

  const handleDelete = (id: number) => {
    deleteKs.mutate(id);
    setSelected(null);
  };

  return (
    <Page fullWidth title="SEO Keywords" subtitle="AI-powered keyword research for your store, products, and pages">
      <Layout>
        <Layout.Section>
          {selected ? (
            <KeywordSetDetail
              keywordSet={selected}
              onBack={() => setSelected(null)}
              onDelete={handleDelete}
            />
          ) : (
            <BlockStack gap="500">
              <GenerateForm />
              <KeywordSetList onSelect={setSelected} />
            </BlockStack>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
